<template>
  <div class="rk-guide-root">
    <transition name="rk-slide">
      <aside v-if="visible && guide && !tourActive" class="rk-guide-card" role="note" aria-live="polite">
        <button class="rk-guide-close" type="button" @click="dismiss" aria-label="Close">
          &times;
        </button>
        <h3 class="rk-guide-title">{{ guide.title }}</h3>
        <p class="rk-guide-summary">{{ guide.summary }}</p>
        <ul v-if="guide.tips && guide.tips.length" class="rk-guide-tips">
          <li v-for="(tip, i) in guide.tips" :key="i">{{ tip }}</li>
        </ul>
        <button class="rk-guide-got-it" type="button" @click="dismiss">Got it, thanks</button>
      </aside>
    </transition>

    <button
      v-if="showHelpButton && guide && !visible && !tourActive"
      class="rk-guide-help-btn"
      type="button"
      @click="reopen"
      aria-label="Show help for this page"
      title="Show help for this page"
    >
      ?
    </button>
  </div>
</template>

<script>
// Mounted once in App.vue and it automatically shows a one-time tip on
// each page, based on pageGuides.js.
//
// It identifies the current page via this.$route.meta.guideKey (set per
// route in router.js), falling back to this.$route.name, then
// this.$route.path. To onboard a new page: add meta: { guideKey: "..." }
// to its route and a matching entry in pageGuides.js — see
// docs/ARCHITECTURE.md. An explicit prop always wins, if you need it:
//   <FirstVisitGuide page-key="AddUnit" />
import { pageGuides } from "./pagesGuide.js";
import { tourState } from "./tourState.js";

const STORAGE_KEY = "rk_seen_guides";

export default {
  name: "FirstVisitGuide",
  props: {
    pageKey: {
      type: String,
      default: null,
    },
    showHelpButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
    };
  },
  computed: {
    resolvedKey() {
      return (
        this.pageKey ||
        (this.$route && this.$route.meta && this.$route.meta.guideKey) ||
        (this.$route && this.$route.name) ||
        (this.$route && this.$route.path) ||
        ""
      );
    },
    guide() {
      // Only on pages behind login — no tips before a user has an
      // account/is signed in (home, login, register, etc.).
      const isAuthPage = !!(this.$route && this.$route.meta && this.$route.meta.auth);
      if (!isAuthPage) return null;
      return pageGuides[this.resolvedKey] || null;
    },
    tourActive() {
      // OnboardingTour.vue is already narrating this page — don't pile a
      // second popup on top of it.
      return tourState.active;
    },
  },
  watch: {
    resolvedKey: {
      immediate: true,
      handler() {
        this.checkFirstVisit();
      },
    },
  },
  methods: {
    getSeen() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      } catch (e) {
        return [];
      }
    },
    markSeen(key) {
      const seen = this.getSeen();
      if (!seen.includes(key)) {
        seen.push(key);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
      }
    },
    checkFirstVisit() {
      if (!this.guide || this.tourActive) {
        this.visible = false;
        return;
      }
      const seen = this.getSeen();
      this.visible = !seen.includes(this.resolvedKey);
    },
    dismiss() {
      this.visible = false;
      this.markSeen(this.resolvedKey);
    },
    reopen() {
      if (this.guide) this.visible = true;
    },
  },
};
</script>

<style scoped>
.rk-guide-root {
  --rk-text: #1f2a24;
  --rk-muted: #5b6b63;
  --rk-accent: #2f6f5e;
  --rk-accent-dark: #234f42;
  --rk-bg: #ffffff;
  --rk-border: #dce3df;
}

.rk-guide-card {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 300px;
  max-width: calc(100vw - 40px);
  background: var(--rk-bg);
  border: 1px solid var(--rk-border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(31, 42, 36, 0.14);
  padding: 18px 18px 16px;
  z-index: 2000;
  color: var(--rk-text);
}

.rk-guide-close {
  position: absolute;
  top: 8px;
  right: 10px;
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: var(--rk-muted);
  cursor: pointer;
  padding: 4px;
}

.rk-guide-close:hover {
  color: var(--rk-text);
}

.rk-guide-title {
  margin: 0 20px 6px 0;
  font-size: 16px;
  font-weight: 600;
}

.rk-guide-summary {
  margin: 0 0 10px;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--rk-muted);
}

.rk-guide-tips {
  margin: 0 0 14px;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--rk-text);
}

.rk-guide-tips li + li {
  margin-top: 4px;
}

.rk-guide-got-it {
  background: var(--rk-accent);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}

.rk-guide-got-it:hover {
  background: var(--rk-accent-dark);
}

.rk-guide-help-btn {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--rk-accent);
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(31, 42, 36, 0.2);
  z-index: 1999;
}

.rk-guide-help-btn:hover {
  background: var(--rk-accent-dark);
}

.rk-slide-enter-active,
.rk-slide-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.rk-slide-enter-from,
.rk-slide-leave-to {
  transform: translateY(12px);
  opacity: 0;
}
</style>